import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../lib/session";
import { hasSupabase } from "../lib/supabase";

/**
 * 초대받은 계정만 로그인한다. 가입 화면은 만들지 않는다.
 *
 * 비밀번호 로그인과 메일 링크 로그인 두 가지를 둔다. 메일 링크는
 * shouldCreateUser: false 로 보내므로 초대되지 않은 주소로는 계정이
 * 만들어지지 않는다.
 */
export default function Login() {
  const { state, signIn, sendMagicLink } = useSession();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  if (!hasSupabase) {
    return (
      <div className="mx-auto max-w-sm py-10">
        <h1 className="text-[26px] font-extrabold tracking-tight">로그인</h1>
        <div className="mt-6 rounded-[24px] border border-line bg-surface p-5 text-[13px] leading-relaxed text-ink-soft">
          이 환경에는 인증이 구성되어 있지 않습니다. 가상 샘플만 볼 수 있는
          골격 모드입니다.
        </div>
        <Link
          to="/"
          className="mt-6 inline-flex rounded-full bg-ink px-6 py-3 text-[14px] font-semibold text-white"
        >
          포털로 돌아가기
        </Link>
      </div>
    );
  }

  if (state.kind === "member") {
    return (
      <div className="mx-auto max-w-sm py-10">
        <h1 className="text-[26px] font-extrabold tracking-tight">이미 로그인됨</h1>
        <p className="mt-3 text-[14px] text-ink-soft">{state.me.email}</p>
        <Link
          to="/properties"
          className="mt-6 inline-flex rounded-full bg-ink px-6 py-3 text-[14px] font-semibold text-white"
        >
          물건 목록으로
        </Link>
      </div>
    );
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
      navigate("/properties");
    } catch (err) {
      setError(err instanceof Error ? err.message : "로그인에 실패했습니다");
    } finally {
      setBusy(false);
    }
  }

  async function submitMagicLink() {
    if (!email.trim()) {
      setError("이메일을 입력하세요");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await sendMagicLink(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "메일을 보내지 못했습니다");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-sm py-10">
      <h1 className="text-[26px] font-extrabold tracking-tight">로그인</h1>
      <p className="mt-3 text-[14px] leading-relaxed text-ink-soft">
        초대받은 계정만 사용할 수 있습니다.
      </p>

      {state.kind === "unapproved" && (
        <p className="mt-5 rounded-2xl border border-warn/30 bg-warn-soft px-4 py-3 text-[13px] leading-relaxed text-warn">
          로그인은 되었지만 승인된 계정이 아닙니다. 관리자에게 승인을 요청하세요.
        </p>
      )}

      <form onSubmit={submitPassword} className="mt-6 space-y-3">
        <input
          className="w-full rounded-2xl border border-line px-4 py-3 text-[14px]"
          type="email"
          autoComplete="email"
          placeholder="이메일"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          className="w-full rounded-2xl border border-line px-4 py-3 text-[14px]"
          type="password"
          autoComplete="current-password"
          placeholder="비밀번호"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-full bg-ink px-6 py-3.5 text-[14px] font-semibold text-white disabled:opacity-50"
        >
          {busy ? "확인 중" : "로그인"}
        </button>
      </form>

      <button
        type="button"
        onClick={submitMagicLink}
        disabled={busy}
        className="mt-3 w-full rounded-full border border-line px-6 py-3.5 text-[14px] font-semibold disabled:opacity-50"
      >
        비밀번호 없이 메일 링크로 받기
      </button>

      {sent && (
        <p className="mt-4 rounded-2xl border border-line bg-surface px-4 py-3 text-[13px] leading-relaxed text-ink-soft">
          로그인 링크를 보냈습니다. 초대된 주소가 아니면 메일이 오지 않습니다.
        </p>
      )}
      {error && (
        <p className="mt-4 rounded-2xl border border-stop/30 bg-stop-soft px-4 py-3 text-[13px] text-stop">
          {error}
        </p>
      )}
    </div>
  );
}
